import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { SendRecipientPicker } from "../shared/SendRecipientPicker";
import { PromangRichTextEditor } from "../../utils/PromangRichTextEditor";
import { postCaseSendCustomerEmail, fetchCaseCustomerEmailStatus } from "../../../api/case";
import { generateText } from "../../../api/ai";
import { normalizeError } from "../../../operationFeedback/normalizeError";
import { hasMeaningfulErrors } from "../../../operationFeedback/types";

function clearStoredRequest(storageKey, requestKey) {
  if (!requestKey) return;
  try { localStorage.removeItem(`${storageKey}:${requestKey}`); } catch {
    // Keeping an already-resolved key is safe: reopening checks its status.
  }
}

function storedRequests(storageKey) {
  const prefix = `${storageKey}:`;
  return Object.keys(localStorage).filter((key) => key.startsWith(prefix))
    .map((key) => localStorage.getItem(key)).filter(Boolean);
}

export function CaseCustomerEmailDialog({
  open,
  onClose,
  caseItem,
  mode = "reply",
  onSent,
}) {
  const { t } = useTranslation();
  const userId = useSelector((state) => state.session.currentUser?.id);
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  // Persist only the correlation key, never recipients, subject or mail body.
  const storageKey = userId && companyId && caseItem?.id
    ? `promang.case-email:${companyId}:${userId}:${caseItem.id}` : null;
  const isReply = mode === "reply";
  const defaultTo = caseItem?.customer?.email || "";
  const defaultSubject = useMemo(() => {
    const num = caseItem?.case_number;
    const title = caseItem?.title || t("case.mail_thread.default_subject");
    return num ? `${title} [SAK-${num}]` : title;
  }, [caseItem?.case_number, caseItem?.title, t]);

  const [to, setTo] = useState(defaultTo);
  const [recipientDraft, setRecipientDraft] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [bodyHtml, setBodyHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [sendCompleted, setSendCompleted] = useState(false);
  const [unknownSend, setUnknownSend] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const requestKey = useRef(null);
  const context = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const contextKey = `${open}:${storageKey}`;
  if (context.current?.key !== contextKey) context.current = { key: contextKey };
  const [formatting, setFormatting] = useState(false);
  const [sendNotice, setSendNotice] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [missingThread, setMissingThread] = useState(null);
  const sendLock = useRef(false);
  const draftCase = useRef(null);

  useEffect(() => {
    if (!open) {
      draftCase.current = null;
      return;
    }
    if (!storageKey || draftCase.current === storageKey) return;
    draftCase.current = storageKey;
    setTo(defaultTo);
    setRecipientDraft("");
    setSubject(defaultSubject);
    setBodyHtml("");
    setSendNotice(null);
    setSendError(null);
    setMissingThread(null);
    setSendCompleted(false);
    setUnknownSend(false);
    requestKey.current = null;
    sendLock.current = false;
    setSending(false);
    setFormatting(false);
    setCheckingStatus(true);
    const current = context.current;
    let savedKey;
    try {
      savedKey = storedRequests(storageKey)[0] || null;
      requestKey.current = savedKey;
    } catch {
      setUnknownSend(true);
      setSendError(t("case.mail_thread.send_storage_failed"));
      setCheckingStatus(false);
      return;
    }
    fetchCaseCustomerEmailStatus(caseItem.id, savedKey).then((res) => {
      if (!mounted.current || context.current !== current) return;
      if (savedKey && res?.status === "sent" && res?.communication?.id) {
        setSendCompleted(true);
        clearStoredRequest(storageKey, requestKey.current);
        requestKey.current = null;
      } else if (savedKey && res?.problem?.outcome === "not_applied") {
        clearStoredRequest(storageKey, requestKey.current);
        requestKey.current = null;
        setUnknownSend(res?.problem?.code === "MAIL_SEND_BLOCKED" || storedRequests(storageKey).length > 0);
        setSendError(res?.problem?.message || t("case.mail_thread.send_failed"));
      } else if (savedKey || res?.status !== "none") {
        setUnknownSend(true);
        setSendError(t(savedKey ? "case.mail_thread.send_unknown" : "case.mail_thread.send_blocked"));
      }
      setCheckingStatus(false);
    }).catch(() => {
      if (!mounted.current || context.current !== current) return;
      setUnknownSend(true);
      setSendError(t("case.mail_thread.send_unknown"));
      setCheckingStatus(false);
    });
  }, [open, caseItem?.id, storageKey, defaultTo, defaultSubject, t]);

  const sendFailureMessage = (res) => {
    if (Array.isArray(res?.errors) && res.errors[0]) return String(res.errors[0]);
    if (res?.error) return String(res.error);
    if (res?.__status) return t("case.mail_thread.send_failed_http", { status: res.__status });
    return t("case.mail_thread.send_failed");
  };

  const handleFormat = async () => {
    const text = String(bodyHtml || "").trim();
    if (!text) {
      toast.error(t("case.mail_thread.ai_needs_text"));
      return;
    }
    const current = context.current;
    setFormatting(true);
    try {
      const res = await generateText({
        text,
        function: "reformat",
        language: "norwegian",
        format: "html",
      });
      if (!mounted.current || context.current !== current) return;
      const next = res?.data?.text;
      if (next) setBodyHtml(next);
      else toast.error(t("case.mail_thread.ai_failed"));
    } catch {
      if (mounted.current && context.current === current) toast.error(t("case.mail_thread.ai_failed"));
    } finally {
      if (mounted.current && context.current === current) setFormatting(false);
    }
  };

  const handleSend = async (newThread = false) => {
    if (!storageKey || !caseItem?.id || sendLock.current || sendCompleted || formatting || unknownSend || checkingStatus) return;
    if (newThread && !missingThread) return;
    if (recipientDraft.trim()) { setSendError(t("recipients.finishInput")); return; }
    if (!String(to || "").trim()) {
      toast.error(t("case.mail_thread.missing_recipient"));
      return;
    }
    if (!String(bodyHtml || "").replace(/<[^>]+>/g, "").trim()) {
      toast.error(t("case.mail_thread.missing_body"));
      return;
    }
    const payload = newThread
      ? { ...missingThread, reply: false }
      : { to, subject, body_html: bodyHtml, reply: isReply };
    const current = context.current;
    requestKey.current = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() :
      `mail-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    payload.request_key = requestKey.current;
    try {
      // Refuse a side effect if its identity cannot survive a reload.
      localStorage.setItem(`${storageKey}:${requestKey.current}`, requestKey.current);
    } catch {
      setSendError(t("case.mail_thread.send_storage_failed"));
      return;
    }
    setSendNotice(null);
    setSendError(null);
    sendLock.current = true;
    setSending(true);
    let sent;
    try {
      const res = await postCaseSendCustomerEmail(caseItem.id, payload);
      if (!mounted.current || context.current !== current) return;
      if (!res || res?.__error || res?.problem || hasMeaningfulErrors(res?.errors) || Number(res?.__status) >= 400) {
        const problem = normalizeError({ response: { status: res?.__status, data: res } });
        if (problem.outcome === "not_applied") {
          clearStoredRequest(storageKey, requestKey.current);
          requestKey.current = null;
        }
        if (problem.code === "MAIL_SEND_BLOCKED") {
          setMissingThread(null);
          setUnknownSend(true);
          setSendError(t("case.mail_thread.send_blocked"));
          return;
        }
        if (res?.__status === 422 && problem.code === "REPLY_MESSAGE_NOT_FOUND" && problem.outcome === "not_applied") {
          const recipient = res.recipient || payload.to;
          setTo(recipient);
          setMissingThread({ ...payload, to: recipient });
          return;
        }
        setMissingThread(null);
        if (problem.outcome !== "not_applied") {
          setUnknownSend(true);
          setSendError(t("case.mail_thread.send_unknown"));
        } else {
          setSendError(sendFailureMessage(res));
        }
        return;
      }
      if (res?.status !== "sent" || !res?.communication?.id) {
        setUnknownSend(true);
        setSendError(t("case.mail_thread.send_unknown"));
        return;
      }
      sent = res;
      clearStoredRequest(storageKey, requestKey.current);
      requestKey.current = null;
      setSendCompleted(true);
      setMissingThread(null);
    } catch {
      if (!mounted.current || context.current !== current) return;
      setMissingThread(null);
      setUnknownSend(true);
      setSendError(t("case.mail_thread.send_unknown"));
    } finally {
      if (mounted.current && context.current === current) {
        if (!sent) sendLock.current = false;
        setSending(false);
      }
    }
    if (sent) {
      toast.success(t("case.mail_thread.sent"));
      // A refresh/callback failure must not claim the email failed or offer to resend.
      try {
        await onSent?.(sent);
        if (mounted.current && context.current === current) onClose?.();
      } catch {
        if (mounted.current && context.current === current) setSendError(t("case.mail_thread.sent_refresh_failed"));
      } finally {
        if (mounted.current && context.current === current) sendLock.current = false;
      }
    }
  };

  const checkStatus = async () => {
    const current = context.current;
    setCheckingStatus(true);
    try {
      // Other dialogs' attempts must be acknowledged before a global "none"
      // can unlock this draft. Their success is never this draft's success.

      if (!requestKey.current) {
        for (const key of storedRequests(storageKey)) {
          const prior = await fetchCaseCustomerEmailStatus(caseItem.id, key);
          if (!mounted.current || context.current !== current) return;
          if (prior?.status === "sent" && prior?.communication?.id) {
            setSendNotice(t("case.mail_thread.previous_send_confirmed"));
            clearStoredRequest(storageKey, key);
          } else if (prior?.problem?.outcome === "not_applied") {
            clearStoredRequest(storageKey, key);
          } else {
            setSendError(t("case.mail_thread.send_unknown"));
            return;
          }
        }
      }
      const res = await fetchCaseCustomerEmailStatus(caseItem.id, requestKey.current);
      if (!mounted.current || context.current !== current) return;
      if (requestKey.current && res?.status === "sent" && res?.communication?.id) {
        clearStoredRequest(storageKey, requestKey.current);
        requestKey.current = null;
        setUnknownSend(false);
        setSendCompleted(true);
        setSendNotice(null);
        setSendError(null);
        try { await onSent?.(res); } catch {
          if (mounted.current && context.current === current) setSendError(t("case.mail_thread.sent_refresh_failed"));
        }
      } else if (!requestKey.current && res?.status === "none") {
        setUnknownSend(false);
        setSendError(null);
      } else if (res?.problem?.outcome === "not_applied") {
        clearStoredRequest(storageKey, requestKey.current);
        requestKey.current = null;
        setUnknownSend(res.problem.code === "MAIL_SEND_BLOCKED" || storedRequests(storageKey).length > 0);
        setSendError(sendFailureMessage(res));
      } else {
        // Missing evidence cannot unlock an already submitted request.
        setSendError(t(requestKey.current ? "case.mail_thread.send_unknown" : "case.mail_thread.send_blocked"));
      }
    } catch {
      if (mounted.current && context.current === current) setSendError(t("case.mail_thread.send_unknown"));
    } finally {
      if (mounted.current && context.current === current) setCheckingStatus(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={() => {
        if (sendLock.current || formatting) return;
        if (missingThread) setMissingThread(null);
        else onClose?.();
      }}
      title={isReply ? "case.mail_thread.reply_title" : "case.mail_thread.compose_title"}
      size="3xl"
      closeOnOutsideClick={!sending}
    >
      <div className="space-y-4">
        {missingThread ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" role="status">
            <p className="font-semibold">{t("case.mail_thread.missing_thread_title")}</p>
            <p className="mt-1 text-sm">{t("case.mail_thread.missing_thread_choice", { to: missingThread.to })}</p>
          </div>
        ) : null}
        {sendNotice ? <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" role="status">
          {sendNotice}
        </div> : null}
        {sendError ? (
          <div
            className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800"
            role="alert"
          >
            {sendError}
          </div>
        ) : null}
        <p className="text-sm text-slate-500">{t("case.mail_thread.signature_hint")}</p>
        <SendRecipientPicker
          key={storageKey}
          label={t("case.mail_thread.to")}
          customerId={caseItem?.customer?.id}
          multiple={false}
          value={to ? [{ email: to, name: to === defaultTo ? caseItem?.customer?.name : "" }] : []}
          options={defaultTo ? [{ key: "customer", name: caseItem?.customer?.displayTitle || caseItem?.customer?.name, email: defaultTo }] : []}
          disabled={sending || Boolean(missingThread) || unknownSend || sendCompleted || checkingStatus}
          onChange={(rows) => setTo(rows[0]?.email || "")}
          onDraftChange={setRecipientDraft}
        />
        <label className="block text-sm font-medium text-slate-700">
          {t("case.mail_thread.subject")}
          <input
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            disabled={sending || Boolean(missingThread) || unknownSend || sendCompleted}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>
        <div className="flex justify-end">
          <button
            type="button"
            disabled={formatting || sending || Boolean(missingThread) || unknownSend || sendCompleted}
            onClick={handleFormat}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <SparklesIcon className="h-4 w-4" />
            {formatting ? t("case.mail_thread.ai_working") : t("case.mail_thread.format_ai")}
          </button>
        </div>
        <fieldset disabled={sending || Boolean(missingThread) || unknownSend || sendCompleted}>
          <PromangRichTextEditor
            value={bodyHtml}
            disabled={sending || Boolean(missingThread) || unknownSend || sendCompleted}
            onChange={(value) => {
              if (!sendLock.current && !missingThread && !unknownSend && !sendCompleted) setBodyHtml(value);
            }}
            placeholder={t("case.mail_thread.body_placeholder")}
            templatesEnabled
            templateButtonLabel={t("case.mail_thread.pick_template")}
            size="lg"
            framed
          />
        </fieldset>
        <div className="flex justify-end gap-2 pt-2">
          {unknownSend ? <button type="button" onClick={checkStatus} disabled={checkingStatus}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">
            {t("case.mail_thread.check_send_status")}
          </button> : null}
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
            onClick={() => missingThread ? setMissingThread(null) : onClose?.()}
            disabled={sending || formatting}
          >
            {t(unknownSend || sendCompleted ? "Lukk" : "Avbryt")}
          </button>
          <button
            type="button"
            className="rounded-lg bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={() => handleSend(Boolean(missingThread))}
            disabled={!storageKey || sending || formatting || sendCompleted || unknownSend || checkingStatus}
          >
            {sendCompleted ? t("case.mail_thread.sent") : sending ? t("case.mail_thread.sending") : t(missingThread ? "case.mail_thread.send_new_thread" : "case.mail_thread.send")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
